import { useInfiniteScroll } from '@vueuse/core'

import type { ComputedRef } from 'vue'
import type { CustomerStageComment } from '@entities/customer'

import { useOnTimezoneChange } from '@entities/viewer'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { useCustomerCommentsStore } from './store'
import { useCustomerCommentsApi } from '../../api/comments/comments.hook'

const SCROLL_DISTANCE = 200

interface CustomerCommentsFeedReturn {
  refComments: ComputedRef<CustomerStageComment[]>
  hasComments: ComputedRef<boolean>
  isLoading: ComputedRef<boolean>
  isLoadingMore: ComputedRef<boolean>
}

/** The All comments feed: the first page on open and on every filter change, further pages on scroll. */
export const useCustomerCommentsFeed = (): CustomerCommentsFeedReturn => {
  const route = useRoute()
  const store = useCustomerCommentsStore()

  const customerId = route.params.id as string
  const pagePath = resolveUrl(AppRoute.CustomersComments, { id: customerId })

  const refComments = computed<CustomerStageComment[]>(() => store.getComments)
  const hasComments = computed<boolean>(() => store.hasComments)
  const isLoading = computed<boolean>(() => store.isLoading)
  const isLoadingMore = computed<boolean>(() => store.isLoadingMore)

  const loadFirstPage = (): void => {
    store.reset()
    void useCustomerCommentsApi({ customerId })
  }

  const loadMore = async (): Promise<void> => {
    await useCustomerCommentsApi({ customerId, cursor: store.getNextCursor })
  }

  onMounted(loadFirstPage)

  watch(() => route.query, () => {
    if (route.path === pagePath) loadFirstPage()
  })

  // Comment dates arrive pre-shifted into the admin's view timezone.
  useOnTimezoneChange(loadFirstPage)

  useInfiniteScroll(window, loadMore, {
    distance: SCROLL_DISTANCE,
    canLoadMore: () => store.hasMore && !store.isLoading && !store.isLoadingMore,
  })

  return {
    refComments,
    hasComments,
    isLoading,
    isLoadingMore,
  }
}
